import * as React from "react";

import { Box } from "../../components/ui/box";
import { Skeleton } from "../../components/ui/skeleton";
import type { Omittable } from "../../lib/props";
import { ROW_RUNGS } from "../../lib/row-rungs";

/** Toolbar row height; single-consumer constant owned by this particle. */
const TOOLBAR_HEIGHT = 40;

// --- Slot names ---

const SLOT_NAMES = {
  left: "DataTableToolbarLeft",
  actions: "DataTableToolbarActions",
} as const;

type SlotKey = keyof typeof SLOT_NAMES;

type Slots = {
  left: React.ReactElement | null;
  actions: React.ReactElement | null;
};

const EMPTY_SLOTS: Slots = { left: null, actions: null };

// --- Slot collection (displayName-based) ---

type ChildWithChildren = React.ReactElement<{ children?: React.ReactNode }>;

function collectSlots(children: React.ReactNode): Slots {
  const slots: Slots = { ...EMPTY_SLOTS };

  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    const element = child as ChildWithChildren;
    if (element.type === React.Fragment) {
      const nested = collectSlots(element.props.children);
      (Object.keys(nested) as SlotKey[]).forEach((key) => {
        if (nested[key] && !slots[key]) slots[key] = nested[key];
      });
      return;
    }
    const displayName = (element.type as { displayName?: string })?.displayName;
    const slotKey = (Object.keys(SLOT_NAMES) as SlotKey[]).find(
      (key) => SLOT_NAMES[key] === displayName,
    );
    if (slotKey && !slots[slotKey]) {
      slots[slotKey] = element;
    }
  });

  return slots;
}

// --- DataTableToolbar ---

type DataTableToolbarProps = Omittable<{
  children: React.ReactNode;
  hidden?: boolean;
}>;

function DataTableToolbar({ children, hidden = false }: DataTableToolbarProps) {
  if (hidden) return null;

  const slots = collectSlots(children);
  const hasLeft = !!slots.left;
  const hasActions = !!slots.actions;
  const justify =
    hasLeft && hasActions ? "between" : hasActions ? "end" : "start";

  return (
    <Box paddingX="md" paddingY="sm" minHeight={TOOLBAR_HEIGHT}>
      <Box direction="row" align="center" justify={justify} gap="xs">
        {slots.left}
        {slots.actions}
      </Box>
    </Box>
  );
}

DataTableToolbar.displayName = "DataTableToolbar";

// --- Slot components ---

type SlotProps = { children?: React.ReactNode };

function DataTableToolbarLeft({ children }: SlotProps) {
  // A gapped cluster, same as the actions slot: the toolbar row is
  // justify-between, so loose children would be spread across the full
  // width instead of holding together at the start edge. The cluster WRAPS
  // when the row cannot hold it (a run of filter chips on a phone): a second
  // line of chips keeps every control reachable, where overflow would push
  // the last ones — and the actions slot after them — past the edge.
  return (
    <Box direction="row" align="center" gap="xs" wrap minWidth={0}>
      {children}
    </Box>
  );
}
DataTableToolbarLeft.displayName = SLOT_NAMES.left;

function DataTableToolbarActions({ children }: SlotProps) {
  return (
    <Box direction="row" align="center" gap="xs">
      {children}
    </Box>
  );
}
DataTableToolbarActions.displayName = SLOT_NAMES.actions;

// --- Skeleton ---

/** The 28px `sm` control rung every toolbar chip sits on. */
const TOOLBAR_CONTROL_HEIGHT = ROW_RUNGS.compactControl;

type DataTableToolbarSkeletonProps = Omittable<{
  /** Chip widths (px) for the framing cluster at the start edge. */
  left?: readonly number[];
  /** Chip widths (px) for the tools cluster at the end edge. */
  actions?: readonly number[];
}>;

/**
 * The toolbar band with anonymous chips in both clusters — a framing pair at
 * the start, a tools pair at the end — at the same height and insets the
 * settled band takes, so a table that arrives under it never shifts. Bars
 * are not `delayed` on their own: the host wraps the whole pane stand-in in
 * one gate so chrome and bars appear together.
 */
function DataTableToolbarSkeleton({
  left = [96, 72],
  actions = [28, 80],
}: DataTableToolbarSkeletonProps) {
  return (
    <Box paddingX="md" paddingY="sm" minHeight={TOOLBAR_HEIGHT} data-slot="data-table-toolbar-skeleton">
      <Box direction="row" align="center" justify="between" gap="xs">
        <Box direction="row" align="center" gap="xs">
          {left.map((width, index) => (
            <Skeleton key={index} width={width} height={TOOLBAR_CONTROL_HEIGHT} className="rounded-md" />
          ))}
        </Box>
        <Box direction="row" align="center" gap="xs">
          {actions.map((width, index) => (
            <Skeleton key={index} width={width} height={TOOLBAR_CONTROL_HEIGHT} className="rounded-md" />
          ))}
        </Box>
      </Box>
    </Box>
  );
}

export {
  DataTableToolbar,
  DataTableToolbarLeft,
  DataTableToolbarActions,
  DataTableToolbarSkeleton,
};
